//
// AHT Weight Management — Semaglutide (Wegovy®) shared screening downstream.
// Single source of truth for the post-confirmation steps shared by the initial
// supply and full re-screen pathways: PGD fresh checks, interaction checks,
// cautions review, supply attestation, and the shared outcomes.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { OutcomeNode, PathwayNode } from "../../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

export const PGD_FRESH_CHECKS = "sem-init-pgd-fresh-checks";
export const Q3_INT_NTI       = "sem-init-int1-narrow-ti";
export const Q4_INT_OCP       = "sem-init-int2-contraceptives";
export const Q5_INT_GLP1      = "sem-init-int3-glp1";
export const Q6_CAUTIONS      = "sem-init-cautions";
export const Q7_ATTEST        = "sem-init-dose-confirm";

// ─────────────────────────────────────────────────────────────────────────────
// Outcome IDs
// ─────────────────────────────────────────────────────────────────────────────

export const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
export const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
export const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Shared downstream nodes — spread into a pathway's nodes array after the
// confirmation / exclusion-question stage. The first node is PGD_FRESH_CHECKS.
// ─────────────────────────────────────────────────────────────────────────────

export const semaglutideScreeningDownstreamNodes: PathwayNode[] = [
  // ───────────────────────────────────────────────────────────────────────────
  // Step 2b — PGD inclusion / exclusion checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: PGD_FRESH_CHECKS,
    type: "pgd_exclusion_check",
    variant: "sequential",
    pgdIdOverride: "pgd-aht-wt-semaglutide",
    anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
    allCleared: Q3_INT_NTI,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 3 — Interaction checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q3_INT_NTI,
    type: "yes_no",
    text: "Is the patient taking any oral medicines with a narrow therapeutic index?",
    hint: "Semaglutide delays gastric emptying — may affect absorption.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the monitoring and management plan for this interaction",
      acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
    },
    yes: Q4_INT_OCP,
    no: Q4_INT_OCP,
    autoAnswer: { when: { hasMedication: ["ciclosporin", "digoxin", "lithium", "phenytoin", "theophylline", "warfarin"] }, value: true },
  },

  {
    id: Q4_INT_OCP,
    type: "yes_no",
    text: "Is the patient using oral contraceptives?",
    hint: "Add barrier method for 4 weeks after starting or any dose escalation. If vomiting/diarrhoea, follow missed pill guidance.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the contraceptive counselling and management plan",
      acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
    },
    yes: Q5_INT_GLP1,
    no: Q5_INT_GLP1,
    autoAnswer: { when: { patientIsFemale: false }, value: false },
  },

  {
    id: Q5_INT_GLP1,
    type: "condition_route",
    conditions: [
      {
        evaluate: (ctx) => ctx.context["sharedConcurrentWeightAgent"] === true,
        then: { outcomeId: OUT_CANNOT_SUPPLY },
      },
    ],
    default: Q6_CAUTIONS,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 4 — Cautions review
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q6_CAUTIONS,
    type: "flag",
    variant: "warn",
    message:
      "Step 4 — Review cautions before supply:\n" +
      "• GI symptoms (nausea, vomiting, diarrhoea) — dehydration risk, especially in elderly\n" +
      "• Gastroparesis symptoms (bloating, abdominal pain) — discontinue if develops\n" +
      "• Pancreatitis suspected — stop immediately; do not restart if confirmed; enzyme elevation alone does not confirm\n" +
      "• Monitor resting heart rate; report palpitations\n" +
      "• Report mood or behavioural changes\n" +
      "• Not a substitute for insulin\n" +
      "• Aspiration risk — advise of delayed gastric emptying before GA or deep sedation\n" +
      "• Animal studies show developmental toxicity — avoid in pregnancy",
    next: Q7_ATTEST,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 5 — Supply attestation
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q7_ATTEST,
    type: "attestation",
    text: "Step 5 — Confirm supply of Wegovy® 0.25mg pre-filled pen",
    attestationStatement:
      "I confirm that I have:\n" +
      "• Supplied 1 × Wegovy® 0.25mg pre-filled pen (4 doses = 28-day supply)\n" +
      "• Checked pen integrity and cold chain before supply\n" +
      "• Confirmed storage: 2–8°C before first use; after first use ≤30°C or refrigerated up to 6 weeks\n" +
      "• Counselled the patient on subcutaneous injection technique and injection site rotation\n" +
      "• Counselled the patient on missed dose guidance\n" +
      "• Advised the patient to inform their GP of initiation\n" +
      "• Provided patient information leaflet\n" +
      "• Recorded batch number and expiry date",
    conclusions: {
      optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
      optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Shared outcomes — used by both the initial supply and re-screen pathways.
// ─────────────────────────────────────────────────────────────────────────────

export const semaglutideScreeningOutcomes: Record<string, OutcomeNode> = {
  [OUT_NO_F2F]: {
    id: OUT_NO_F2F,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — face-to-face consultation required",
    reason:
      "Initial supply under this PGD requires the patient to attend in person at AHT registered premises.",
    safetyNetting: [
      "Ask the patient to book an in-person appointment to proceed with initial supply.",
    ],
  },
  [OUT_CANNOT_SUPPLY]: {
    id: OUT_CANNOT_SUPPLY,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — Wegovy® (semaglutide)",
    reason:
      "An inclusion criterion was not met, an exclusion criterion was triggered, or a concurrent GLP-1 agonist was identified. Supply cannot proceed under this PGD.",
    safetyNetting: [
      "Discuss the reason supply cannot proceed with the patient.",
      "Consider referral to prescriber or GP if clinically appropriate.",
    ],
  },
  [OUT_SUPPLY]: {
    id: OUT_SUPPLY,
    type: "outcome",
    outcomeType: "supply",
    title: "Supply complete — Wegovy® 0.25mg (initial supply)",
    reason:
      "All inclusion criteria met, no exclusion criteria triggered, and supply attestation confirmed.",
    drugRef: "semaglutide",
    safetyNetting: [
      "Return in 4 weeks for monthly monitoring review.",
      "Missed dose: if ≤5 days late, give now and resume usual schedule. If >5 days late, skip and resume next scheduled dose. Dosing day may be changed as long as ≥3 days between doses.",
      "Contact the service immediately if any signs of allergic reaction, severe abdominal pain, or persistent vomiting.",
      "Seek urgent medical attention for signs of pancreatitis: severe, persistent abdominal pain radiating to the back.",
      "Store unused pens in the refrigerator (2–8°C). After first use, may store at ≤30°C or refrigerated for up to 6 weeks.",
    ],
  },
};
